import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdDownload,
  MdInfo,
  MdOutlineDescription,
  MdOutlineInventory2,
  MdUpload,
} from "react-icons/md";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import { useLazyQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { CONTENT_TYPES } from "utils/constants";

export default function DocumentsSection({
  hasNotice,
  uploadingNotice,
  onNoticeUpload,
  noticeAttachment,
  selectedProducts,
  products,
}) {
  const [productDocuments, setProductDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchAttachments] = useLazyQuery(graphql.queries.ATTACHMENTS, {
    fetchPolicy: "network-only",
  });
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.COMPANY);
  const DOCUMENTS_SECTION = t("DOCUMENTS_SECTION", { returnObjects: true });

  const loadAllDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const docsData = [];

      for (const productId of selectedProducts) {
        const product = products.find((p) => p.id === productId);
        if (!product) {
          continue;
        }

        try {
          const result = await fetchAttachments({
            variables: {
              where: {
                insurancePlans: {
                  id: productId,
                },
                project: { id: null },
              },
            },
          });

          if (result?.errors?.length) {
            throw result.errors[0];
          }

          if (result?.error) {
            throw result.error;
          }

          const staticDocs = result?.data?.attachments?.data || [];

          docsData.push({
            product,
            staticDocs,
          });
        } catch (error) {
          console.error(
            "Failed to fetch attachments for product",
            productId,
            error,
          );
          docsData.push({
            product,
            staticDocs: [],
          });
        }
      }

      setProductDocuments(docsData);
    } catch (error) {
      console.error("Failed to load product documents", error);
      setProductDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [fetchAttachments, products, selectedProducts]);

  useEffect(() => {
    if (selectedProducts.length > 0) {
      loadAllDocuments();
    } else {
      setProductDocuments([]);
    }
  }, [selectedProducts, loadAllDocuments]);

  const totalStaticDocs = productDocuments.reduce(
    (sum, pd) => sum + pd.staticDocs.length,
    0,
  );

  const noticeFileName = useMemo(
    () => noticeAttachment?.name,
    [noticeAttachment?.name],
  );

  const handleUploaderChange = async (fileList) => {
    if (uploadingNotice) return;
    const [file] = fileList || [];
    if (!file) return;
    onNoticeUpload?.(file);
  };

  return (
    <div className="company__space-y-8">
      <div className="company__info-box company__p-4 company__flex company__gap-3">
        <MdInfo
          className="company__w-5 company__h-5 company__text-blue-600 company__flex-shrink-0"
          style={{ marginTop: "0.125rem" }}
        />
        <div className="company__text-sm company__text-blue-900">
          <p className="company__font-medium company__mb-1">
            {DOCUMENTS_SECTION.INFO_BOX.TITLE}
          </p>
          <p>
            {DOCUMENTS_SECTION.INFO_BOX.DESCRIPTION}{" "}
            <span className="company__font-semibold">
              {DOCUMENTS_SECTION.INFO_BOX.CATALOG_LINK}
            </span>
            .
          </p>
        </div>
      </div>

      <div>
        <h3 className="company__text-lg company__font-semibold company__text-slate-900 company__mb-4">
          {DOCUMENTS_SECTION.NOTICE.TITLE}
        </h3>
        <p className="company__text-sm company__text-slate-600 company__mb-4">
          {DOCUMENTS_SECTION.NOTICE.DESCRIPTION}
        </p>

        <div className="company__border company__border-slate-200 company__rounded-lg company__p-4">
          {hasNotice ? (
            <div className="company__flex company__flex-col company__gap-4 company__sm-flex-row company__sm-items-center company__sm-justify-between">
              <div className="company__flex company__items-center company__gap-3">
                <MdOutlineDescription className="company__w-8 company__h-8 company__text-green-600" />
                <div>
                  <div className="company__font-medium company__text-slate-900">
                    {DOCUMENTS_SECTION.NOTICE.DOWNLOADED_LABEL}
                  </div>
                  <div className="company__text-sm company__text-slate-600">
                    {noticeFileName || DOCUMENTS_SECTION.NOTICE.NO_FILENAME}
                  </div>
                </div>
              </div>
              <div className="company__flex company__items-center company__gap-3">
                <span className="company__px-3 company__py-1 company__bg-green-100 company__text-green-700 company__text-xs company__font-medium company__rounded-full">
                  {DOCUMENTS_SECTION.NOTICE.STATUS.CONFIGURED}
                </span>
                <Input
                  type="file"
                  variant="uploader"
                  showUploadList={false}
                  maxCount={1}
                  validTypes={[CONTENT_TYPES.PDF]}
                  onUpload={handleUploaderChange}
                  disabled={uploadingNotice}
                >
                  <Button
                    type="primary--link"
                    disabled={uploadingNotice}
                    size="small"
                  >
                    {uploadingNotice
                      ? DOCUMENTS_SECTION.NOTICE.BUTTON.UPLOADING
                      : DOCUMENTS_SECTION.NOTICE.BUTTON.REPLACE}
                  </Button>
                </Input>
              </div>
            </div>
          ) : (
            <Input
              type="file"
              fillWidth
              showUploadList={false}
              maxCount={1}
              validTypes={[CONTENT_TYPES.PDF]}
              onUpload={handleUploaderChange}
              disabled={uploadingNotice}
            >
              <div className="company__flex company__items-center company__justify-center company__w-full company__px-4 company__py-8 company__border-2 company__border-dashed company__border-slate-300 company__rounded-lg company__cursor-pointer company__hover-border-blue-400 company__hover-bg-blue-50 company__transition-colors company__p-6">
                <div className="company__text-center">
                  <MdUpload className="company__w-8 company__h-8 company__text-slate-400 company__mx-auto company__mb-2" />
                  <p className="company__text-sm company__text-slate-600">
                    {uploadingNotice
                      ? DOCUMENTS_SECTION.NOTICE.BUTTON.UPLOADING
                      : DOCUMENTS_SECTION.NOTICE.BUTTON.UPLOAD}
                  </p>
                </div>
              </div>
            </Input>
          )}
        </div>
      </div>

      {loading ? (
        <div className="company__text-center company__py-8">
          <div className="company__loading-spinner"></div>
          <p className="company__text-sm company__text-slate-600 company__mt-2">
            {DOCUMENTS_SECTION.STATE.LOADING}
          </p>
        </div>
      ) : selectedProducts.length === 0 ? (
        <div className="company__p-8 company__bg-slate-50 company__border company__border-slate-200 company__rounded-lg company__text-center">
          <MdOutlineInventory2 className="company__w-12 company__h-12 company__text-slate-400 company__mx-auto company__mb-3" />
          <p className="company__text-slate-600">
            {DOCUMENTS_SECTION.EMPTY.TITLE}
          </p>
          <p className="company__text-sm company__text-slate-500 company__mt-1">
            {DOCUMENTS_SECTION.EMPTY.DESCRIPTION}
          </p>
        </div>
      ) : (
        <>
          <div className="company__space-y-6">
            {productDocuments.map(({ product, staticDocs }) => (
              <div
                key={product.id}
                className="company__border company__border-slate-200 company__rounded-lg company__overflow-hidden"
              >
                <div className="company__px-4 company__py-3 company__bg-slate-100 company__border-b company__border-slate-200">
                  <div className="company__flex company__items-center company__gap-2">
                    <MdOutlineInventory2 className="company__w-5 company__h-5 company__text-slate-600" />
                    <h4 className="company__font-semibold company__text-slate-900">
                      {product.name}
                    </h4>
                  </div>
                </div>

                <div className="company__p-4 company__space-y-6">
                  <div>
                    <h5 className="company__font-medium company__text-slate-900 company__mb-3 company__flex company__items-center company__gap-2">
                      <MdDownload className="company__w-4 company__h-4 company__text-blue-600" />
                      {t("DOCUMENTS_SECTION.PRODUCTS.STATIC_DOCS.TITLE", {
                        count: staticDocs.length,
                      })}
                    </h5>
                    {staticDocs.length === 0 ? (
                      <p
                        className="company__text-sm company__text-slate-500"
                        style={{ fontStyle: "italic" }}
                      >
                        {DOCUMENTS_SECTION.PRODUCTS.STATIC_DOCS.EMPTY}
                      </p>
                    ) : (
                      <div className="company__space-y-2">
                        {staticDocs.map((doc) => (
                          <div
                            key={doc.id}
                            className="company__flex company__items-center company__gap-3 company__p-3 company__bg-blue-50 company__border company__border-blue-200 company__rounded-lg"
                          >
                            <MdOutlineDescription className="company__w-5 company__h-5 company__text-blue-600 company__flex-shrink-0" />
                            <div className="company__flex-1">
                              <div className="company__font-medium company__text-slate-900 company__text-sm">
                                {doc.name}
                              </div>
                              {doc.description && (
                                <div className="company__text-xs company__text-slate-600">
                                  {doc.description}
                                </div>
                              )}
                            </div>
                            {doc.is_mandatory && (
                              <span className="company__px-2 company__py-1 company__bg-blue-600 company__text-white company__text-xs company__rounded">
                                {
                                  DOCUMENTS_SECTION.PRODUCTS.STATIC_DOCS
                                    .MANDATORY
                                }
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Documents dynamiques et justificatifs à fournir temporairement désactivés */}
                </div>
              </div>
            ))}
          </div>

          <div className="company__p-4 company__bg-slate-50 company__border company__border-slate-200 company__rounded-lg">
            <h4 className="company__font-medium company__text-slate-900 company__mb-2">
              {DOCUMENTS_SECTION.SUMMARY.TITLE}
            </h4>
            <div className="company__text-sm company__text-slate-700 company__space-y-1">
              <p>
                {DOCUMENTS_SECTION.SUMMARY.BULLET}{" "}
                <strong>{DOCUMENTS_SECTION.SUMMARY.PRODUCTS}</strong>{" "}
                {productDocuments.length}
              </p>
              <p>
                {DOCUMENTS_SECTION.SUMMARY.BULLET}{" "}
                <strong>{DOCUMENTS_SECTION.SUMMARY.NOTICE}</strong>{" "}
                {hasNotice
                  ? DOCUMENTS_SECTION.NOTICE.STATUS.CONFIGURED
                  : DOCUMENTS_SECTION.NOTICE.STATUS.MISSING}
              </p>
              <p>
                {DOCUMENTS_SECTION.SUMMARY.BULLET}{" "}
                <strong>{DOCUMENTS_SECTION.SUMMARY.STATIC_DOCS}</strong>{" "}
                {totalStaticDocs} {DOCUMENTS_SECTION.SUMMARY.STATIC_DOCS_SUFFIX}
              </p>
              {/* <p>
                • <strong>Documents dynamiques :</strong> {totalDynamicDocs}{" "}
                document(s)
              </p>
              <p>
                • <strong>Justificatifs requis :</strong> {totalSupportingDocs}{" "}
                document(s)
              </p> */}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
